
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, MapPin, ArrowRight } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import ButtonAnimation from '@/components/ui/ButtonAnimation';
import PropertyCard from '@/components/ui/PropertyCard';


import {
  getFeaturedProperties,
  getLocalities,
  getTrendingProperties,
  getUpcomingProperties
} from '@/services/property';
import { developers } from '@/services/developersData';
import { localities as localityData } from '@/services/localityData';
import { getAllPublishedBlogPosts } from '@/services/blog';
import { PropertyMap } from '@/components/ProppertyMap';
import { propertyTypes, localities, pagination } from '@/constants/index';
import { PropertyCardSkeleton, LocationCardSkeleton, BlogCardSkeleton } from '@/components/skeletons/HomeSkeletons';
import { encodeS3Url } from '@/components/encodeS3Url';
import { capitalizeWords, toSentenceCase } from '@/helper';


const Index = () => {
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  // State for fetched data
  const [featuredProperties, setFeaturedProperties] = useState<any[]>([]);
  const [trendingProperties, setTrendingProperties] = useState<any[]>([]);
  const [upcomingProperties, setUpcomingProperties] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [blogs, setBlogs] = useState<any[]>([]);

  // Loading States
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [loadingTrending, setLoadingTrending] = useState(true);
  const [loadingUpcoming, setLoadingUpcoming] = useState(true);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [loadingBlogs, setLoadingBlogs] = useState(true);


  // Search Filter State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPropertyType, setSelectedPropertyType] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedPriceRange, setSelectedPriceRange] = useState('all');

  const handleSearch = () => {
    const params = new URLSearchParams();

    if (searchQuery) {
      params.append('search', searchQuery);
    }

    if (selectedPropertyType && selectedPropertyType !== 'all') {
      params.append('type', selectedPropertyType);
    }

    if (selectedLocation && selectedLocation !== 'all') {
      params.append('location', selectedLocation); // Assuming backend expects 'location' param
    }

    if (selectedPriceRange && selectedPriceRange !== 'all') {
      if (selectedPriceRange === '5000000+') {
        params.append('minPrice', '5000000');
      } else {
        const [min, max] = selectedPriceRange.split('-');
        if (min) params.append('minPrice', min);
        if (max) params.append('maxPrice', max);
      }
    }

    navigate(`/properties?${params.toString()}`);
  };

  const fetchFeaturedProperties = async () => {
    setLoadingFeatured(true);
    try {
      const { data } = await getFeaturedProperties({
        page: pagination.page,
        limit: pagination.cardLimit,
      });
      if (Array.isArray(data)) {
        setFeaturedProperties(data);
      } else {
        console.error("Unexpected API response format:", data);
      }
    } catch (error) {
      console.log("Error while fetching featured properties", error);
    } finally {
      setLoadingFeatured(false);
    }
  }
  const fetchTrendingProperties = async () => {
    setLoadingTrending(true);
    try {
      const { data } = await getTrendingProperties({
        page: pagination.page,
        limit: pagination.cardLimit,
      });
      if (Array.isArray(data)) {
        setTrendingProperties(data);
      } else {
        console.error("Unexpected API response format:", data);
      }
    } catch (error) {
      console.log("Error while fetching trending properties", error);
    } finally {
      setLoadingTrending(false);
    }
  }
  const fetchUpcomingProperties = async () => {
    setLoadingUpcoming(true);
    try {
      const { data } = await getUpcomingProperties({
        page: pagination.page,
        limit: pagination.cardLimit,
      });
      if (Array.isArray(data)) {
        setUpcomingProperties(data);
      } else {
        console.error("Unexpected API response format:", data);
      }
    } catch (error) {
      console.log("Error while fetching upcoming properties", error);
    } finally {
      setLoadingUpcoming(false);
    }
  }
  const fetchLocalities = async () => {
    setLoadingLocations(true);
    try {
      const data = await getLocalities({ limit: 6 });

      const mappedLocations = data?.localities?.map((loc: any) => {
        const staticData = localityData.find(l => l.name.toLowerCase() === loc.locality.toLowerCase()
          || l.slug.toLowerCase() === loc.locality.toLowerCase());
        return {
          ...loc,
          slug: staticData?.slug || loc.locality.toLowerCase().replace(/\s+/g, '-'),
          image: staticData?.projectImage || "/placeholder.svg"
        };
      }) || [];

      setLocations(mappedLocations);
    } catch (error) {
      console.log("Error while fetching localities", error);
    } finally {
      setLoadingLocations(false);
    }
  }
  const fetchBlogs = async () => {
    setLoadingBlogs(true);
    try {
      const { data } = await getAllPublishedBlogPosts({
        page: pagination.page,
        limit: pagination.cardLimit,
      });

      if (Array.isArray(data)) {
        setBlogs(data);
      } else {
        console.error("Unexpected API response format:", data);
      }
    } catch (error) {
      console.log("Error while fetching blogs", error);
    } finally {
      setLoadingBlogs(false);
    }
  }

  // Fetch all landing page data
  useEffect(() => {
    setIsLoaded(true);
    // Fetch Featured Properties
    fetchFeaturedProperties();
    // Fetch Trending Properties
    fetchTrendingProperties();
    // Fetch Upcoming Properties
    fetchUpcomingProperties();
    // Fetch Locations
    fetchLocalities();
    // Fetch blogs
    fetchBlogs();
  }, []);

  // Fade in sections on scroll
  useEffect(() => {
    const fadeElements = document.querySelectorAll('.fade-in-section');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, { threshold: 0.1 });

    fadeElements.forEach(element => {
      observer.observe(element);
    });

    return () => {
      fadeElements.forEach(element => {
        observer.unobserve(element);
      });
    };
  }, []);

  const heroImages = [
    "/image/dubai-palm-jumeirah-island-aerial-view.avif",
    "/image/dubai-downtown-skyline.avif",
    "/image/blue-hour.avif"
  ];

  const [currentHeroImageIndex, setCurrentHeroImageIndex] = useState(0);

  // Hero Slider Effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 md:pt-32 pb-20 overflow-hidden">
        {/* Parallax Background */}
        <div
          className="absolute inset-0 z-0 will-change-transform"
        >
          {/* Image Slider */}
          <div className="absolute inset-0 w-full h-full">

            {/* Hero Image Slider */}
            {heroImages.map((image, index) => (
              <img
                key={index}
                src={image}
                alt={`Luxury Property ${index + 1}`}
                className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-1000 ease-in-out ${index === currentHeroImageIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                  }`}
              />
            ))}

          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/20 z-20"></div>

        </div>

        <div className="container relative z-10 mx-auto px-4 md:px-6 flex flex-col justify-center h-full">
          <div className={`max-w-4xl mx-auto text-center space-y-6 md:space-y-8 ${isLoaded ? 'animate-fade-in' : 'opacity-0'}`}>
            <span className="inline-block py-1.5 px-4 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white font-medium mb-2 animate-slide-up opacity-0 text-xs md:text-sm tracking-widest uppercase" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
              Dubai Premium Real Estate
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-[1.1] tracking-tight animate-slide-up opacity-0"
              style={{ animationDelay: '0.2s', animationFillMode: 'forwards', textShadow: '0 4px 30px rgba(0,0,0,0.5)' }}>
              Discover Your Dream <br className="hidden md:block" /> Property in <span className="text-gold inline-block relative">Dubai
                <svg className="absolute w-full h-3 -bottom-1 left-0 text-gold opacity-60" viewBox="0 0 100 10" preserveAspectRatio="none">
                  <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="2" fill="none" />
                </svg>
              </span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed animate-slide-up opacity-0 px-4"
              style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
              Explore exclusive properties in Dubai's most prestigious neighborhoods.
              Your journey to luxury living starts here.
            </p>

            {/* Search Form */}
          </div>

          <div className="max-w-4xl mx-auto mt-20 w-full">
            <div className="w-full glass-card-strong p-4 md:p-8 rounded-2xl border-white/10 shadow-2xl animate-slide-up opacity-0" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
              <div className="mb-3 md:mb-4 relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 z-10" />
                <Input
                  type="text"
                  placeholder="Search Properties"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full bg-white/90 text-black border-none placeholder:text-gray-500 h-10 md:h-14 text-sm md:text-base rounded-full focus-visible:ring-gold shadow-sm"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4">
                <div>
                  <Select value={selectedPropertyType} onValueChange={setSelectedPropertyType}>
                    <SelectTrigger aria-label="Select Property Type" className="h-10 md:h-12 bg-white/90 border-none text-black rounded-xl text-sm md:text-base">
                      <SelectValue placeholder="Property Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Properties</SelectItem>
                      {propertyTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                    <SelectTrigger aria-label="Select Location" className="h-10 md:h-12 bg-white/90 border-none text-black rounded-xl text-sm md:text-base">
                      <SelectValue placeholder="Location" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Locations</SelectItem>
                      {localities.map((loc) => (
                        <SelectItem key={loc.value} value={loc.label}>
                          {capitalizeWords(loc.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Select value={selectedPriceRange} onValueChange={setSelectedPriceRange}>
                    <SelectTrigger aria-label="Select Price Range" className="h-10 md:h-12 bg-white/90 border-none text-black rounded-xl text-sm md:text-base">
                      <SelectValue placeholder="Price Range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any Price</SelectItem>
                      <SelectItem value="0-1000000">Up to 1M AED</SelectItem>
                      <SelectItem value="1000000-3000000">1M - 3M AED</SelectItem>
                      <SelectItem value="3000000-5000000">3M - 5M AED</SelectItem>
                      <SelectItem value="5000000+">5M+ AED</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <ButtonAnimation className="w-full btn-gold h-10 md:h-12 text-sm md:text-base" onClick={handleSearch}>
                    <Search className="h-3 w-3 md:h-4 md:w-4 mr-2" />
                    Search
                  </ButtonAnimation>
                </div>
              </div>
            </div>

            {/* <div className="flex flex-wrap gap-5 justify-center animate-slide-up opacity-0" style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}>
              <ButtonAnimation
                variant="outline"
                className="bg-white/10 backdrop-blur-md text-white border-white/30 hover:bg-white/20 px-8 py-6 text-lg rounded-xl transition-all duration-300 hover:scale-105"
                onClick={() => navigate('/properties')}
              >
                View Properties
              </ButtonAnimation>
              <ButtonAnimation
                variant="default"
                className="btn-gold px-8 py-6 text-lg rounded-xl shadow-lg shadow-gold/20 transition-all duration-300 hover:scale-105"
                onClick={() => navigate('/contact')}
              >
                Contact Us
              </ButtonAnimation>
            </div> */}
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      {(!loadingFeatured && featuredProperties?.length === 0) ? null : (
        <section className="py-12 md:py-20">
          <div className="container mx-auto px-4 md:px-6">
            <div className="fade-in-section">
              <div className="flex justify-between items-center mb-8 md:mb-10">
                <div>
                  <span className="text-gold font-medium">Curated Selection</span>
                  <h2 className="text-3xl md:text-4xl font-bold mt-1">Featured Properties</h2>
                </div>
                <Link
                  to="/properties?filter=featured"
                  className="hidden md:flex items-center text-primary hover:text-gold transition-colors"
                >
                  View All <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
                {loadingFeatured ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-full">
                      <PropertyCardSkeleton />
                    </div>
                  ))
                ) : (
                  featuredProperties?.map((property, index) => (
                    <div key={property.id}
                    // className={`${getDelayClass(index)} transform transition-all`}
                    >
                      <PropertyCard type="featured" property={property} />
                    </div>
                  ))
                )}
              </div>

              <div className="mt-10 text-center md:hidden">
                <Link
                  to="/properties?filter=featured"
                  className="btn-outline inline-flex items-center"
                >
                  View All Properties <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Trending Properties */}
      {(!loadingTrending && trendingProperties?.length === 0) ? null : (
        <section className="py-12 md:py-20">
          <div className="container mx-auto px-4 md:px-6">
            <div className="fade-in-section">
              <div className="flex justify-between items-center mb-8 md:mb-10">
                <div>
                  <span className="text-gold font-medium">Hot Right Now</span>
                  <h2 className="text-3xl md:text-4xl font-bold mt-1">Trending Properties</h2>
                </div>
                <Link
                  to="/properties?filter=trending"
                  className="hidden md:flex items-center text-primary hover:text-gold transition-colors"
                >
                  View All <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
                {loadingTrending ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-full">
                      <PropertyCardSkeleton />
                    </div>
                  ))
                ) : (
                  trendingProperties?.map((property, index) => (
                    <div key={property.id}
                    // className={`${getDelayClass(index)} transform transition-all`}
                    >
                      <PropertyCard type="trending" property={property} />
                    </div>
                  ))
                )}
              </div>

              <div className="mt-10 text-center md:hidden">
                <Link
                  to="/properties?filter=trending"
                  className="btn-outline inline-flex items-center"
                >
                  View All Trending <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Properties */}
      {(!loadingUpcoming && upcomingProperties?.length === 0) ? null : (
        <section className="py-12 md:py-20">
          <div className="container mx-auto px-4 md:px-6">
            <div className="fade-in-section">
              <div className="flex justify-between items-center mb-8 md:mb-10">
                <div>
                  <span className="text-gold font-medium">Future Investments</span>
                  <h2 className="text-3xl md:text-4xl font-bold mt-1">Upcoming Projects</h2>
                </div>
                <Link
                  to="/properties?filter=off-plan"
                  className="hidden md:flex items-center text-primary hover:text-gold transition-colors"
                >
                  View All <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
                {loadingUpcoming ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-full">
                      <PropertyCardSkeleton />
                    </div>
                  ))
                ) : (
                  upcomingProperties?.map((property, index) => (
                    <div key={property.id}
                    // className={`${getDelayClass(index)} transform transition-all`}
                    >
                      <PropertyCard property={property} />
                      <></>
                    </div>
                  ))
                )}
              </div>

              <div className="mt-10 text-center md:hidden">
                <Link
                  to="/properties?filter=off-plan"
                  className="btn-outline inline-flex items-center"
                >
                  View All Upcoming <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Developers Section */}
      <section className="py-12 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="fade-in-section">
            <div className="text-center mb-8 md:mb-12">
              <span className="text-gold font-medium">Premium Builders</span>
              <h2 className="text-2xl md:text-4xl font-bold mt-1 mb-3">Featured Developers</h2>
              <p className="max-w-2xl mx-auto text-muted-foreground text-sm md:text-base">
                We partner with Dubai's most prestigious developers to bring you exceptional properties
                built with the highest standards of quality and design.
              </p>
            </div>

            <div className="flex flex-wrap justify-center items-center gap-6 md:gap-12">
              {developers?.map((developer, index) => (
                <Link
                  key={developer.slug}
                  to={`/developers/${developer.slug}`}
                  className={`group hover:scale-105 transition-transform duration-300}`}
                >
                  <div className="p-4 grayscale hover:grayscale-0 transition-all duration-500 bg-white/5 rounded-lg">
                    <img
                      src={developer.logo}
                      alt={developer.name}
                      className="h-10 md:h-12 max-w-[120px] md:max-w-[150px] object-contain"
                    />
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-10 text-center">
              <Link
                to="/developers"
                className="btn-outline inline-flex items-center"
              >
                View All Developers <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="fade-in-section">
            <div className="text-center mb-8 md:mb-12">
              <span className="text-gold font-medium">Prime Areas</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-1 mb-4">Popular Locations</h2>
              <p className="max-w-2xl mx-auto text-muted-foreground">
                Discover properties in Dubai's most desirable neighborhoods, each offering a unique lifestyle
                and exceptional amenities.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {loadingLocations ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-full">
                    <LocationCardSkeleton />
                  </div>
                ))
              ) : (
                locations?.map((location, index) => (
                  <Link
                    key={location.locality}
                    to={`/locations/${location.slug}`}
                    className={`group relative overflow-hidden rounded-lg shadow-md h-48`}
                  >
                    <img
                      src={location.image}
                      alt={capitalizeWords(location.locality)}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                    <div className="absolute inset-0 flex flex-col justify-end p-6">
                      <div className="flex items-center mb-2">
                        <MapPin className="text-gold mr-2 h-5 w-5" />
                        <h3 className="text-xl font-bold text-white">{capitalizeWords(location.locality)}</h3>
                      </div>
                      <p className="text-white/80">{location.count} Properties</p>
                    </div>
                  </Link>
                ))
              )}
            </div>

            <div className="mt-10 text-center">
              <Link
                to="/locations"
                className="btn-outline inline-flex items-center"
              >
                Explore All Locations <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-12 md:py-24 relative bg-primary text-white">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1451976426598-a7593bd6d0b2?auto=format&q=75&w=2000')] bg-cover bg-center opacity-20"></div>
        <div className="container relative z-10 mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center fade-in-section">
            <span className="text-gold font-medium">Expert Guidance</span>
            <h2 className="text-3xl md:text-5xl font-bold mt-2 mb-6">Ready to Find Your <br className="hidden md:block" /> Perfect Property?</h2>
            <p className="text-lg md:text-xl text-white/80 mb-8 md:mb-10">
              Our team of experienced real estate professionals is here to help you navigate
              Dubai's luxury property market and find your ideal investment.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <ButtonAnimation
                variant="default"
                className="btn-gold"
                onClick={() => navigate('/contact')}
              >
                Contact Us
              </ButtonAnimation>
              <ButtonAnimation
                variant="outline"
                className="bg-white/10 backdrop-blur-sm text-white border-white/30 hover:bg-white/20"
                onClick={() => navigate('/properties')}
              >
                View Properties
              </ButtonAnimation>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      {(!loadingBlogs && blogs?.length === 0) ? null : (
        <section className="py-12 md:py-20">
          <div className="container mx-auto px-4 md:px-6">
            <div className="fade-in-section">
              <div className="text-center mb-8 md:mb-12">
                <span className="text-gold font-medium">Latest News</span>
                <h2 className="text-3xl md:text-4xl font-bold mt-1 mb-4">Blogs & Updates</h2>
                <p className="max-w-2xl mx-auto text-muted-foreground">
                  Stay informed about Dubai's real estate market with our latest articles, trends analysis,
                  and expert insights.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {loadingBlogs ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-full">
                      <BlogCardSkeleton />
                    </div>
                  ))
                ) : (
                  blogs?.map((blog, index) => (
                    <Link
                      key={blog._id}
                      to={`/blogs/${blog.slug || blog._id}`}
                    // className={`group ${getDelayClass(index)}`}
                    >
                      <div className="overflow-hidden rounded-lg shadow-md transition-all duration-300 bg-white hover:shadow-lg h-full flex flex-col">
                        <div className="relative h-48 overflow-hidden shrink-0">
                          <img
                            src={blog.featuredImage ? encodeS3Url(blog.featuredImage) : "/placeholder.svg"}
                            alt={blog.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                        </div>
                        <div className="p-6 flex-1 flex flex-col">
                          <div className="text-sm text-muted-foreground mb-2">
                            {new Date(blog.publishDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </div>
                          <div className="h-14 mb-2">
                            <h3 className="text-xl font-bold line-clamp-2 group-hover:text-gold transition-colors">
                              {capitalizeWords(blog.title)}
                            </h3>
                          </div>
                          <div className="h-20 mb-4">
                            <p className="text-muted-foreground line-clamp-3">
                              {toSentenceCase(blog.excerpt)}
                            </p>
                          </div>
                          <div className="flex items-center text-primary font-medium group-hover:text-gold transition-colors mt-auto">
                            Read More <ArrowRight className="ml-2 h-4 w-4" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))
                )}
              </div>

              <div className="mt-10 text-center">
                <Link
                  to="/blogs"
                  className="btn-outline inline-flex items-center"
                >
                  View All Articles <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Map section */}
      <PropertyMap />

    </>
  );
};

export default Index;

